/**
 * The inspection's electrical devices as objects of the structure.
 *
 * The existing-conditions pass reports, per surface, the outlets, switches,
 * lights and fans the walkthrough recording showed (`doc.devices[]`, each at
 * a `world` point). Until this step nothing put them in a room: the
 * electrical changes layer read them straight off the docs. Here each one
 * becomes a room object of the type the app already draws and prices, so the
 * scan as found carries its real electrical instead of a guess.
 *
 * The guesses are the pipeline's: the recessed-can grid (`add_lighting`, run
 * by post-merge processing on every scan) and the light bar bathroom
 * post-processing hangs over every vanity mirror. In a room where the
 * inspection found a real ceiling light, the generated cans are removed; in a
 * room where it found a real wall light, the generated vanity bars are. The
 * room has the lights it has, not the lights a rule would give it. A room
 * where none of that kind was found keeps its guesses (the user, 2026-10-16:
 * a found sconce stood in the same spot as the generated bar, two lights
 * where the bathroom has one).
 *
 * Electrical only (the user, 2026-10-16): vents, detectors, drains and the
 * rest have no object type or drawing yet.
 *
 * Pure: no I/O, no mutation of the input.
 */

const { SCANNED_FIELD } = require('../scanned-label/index');
const {
  makeCeilingYLookup,
  makeCeilingIdLookup,
} = require('../lighting/ceilingSeat');
const { pruneDanglingGroupLinks } = require('../group-customization/roomLinks');
const { activeReview } = require('./inspectionReview');
const {
  GENERIC_DOME,
  genericCeilingLightBuilds,
} = require('../lighting/genericCeilingLights');
const { polygonOf } = require('./roomOutline');
const { isPointInsideOrOnPolygon } = require('../geometry/pointInPolygon');

/** `addedBy` on every object this step makes; a re-apply replaces them. */
const INSPECTED_PRODUCER = 'existing-conditions';

/** `addedBy` of the pipeline's generated ceiling-light grid. */
const GENERATED_LIGHT_PRODUCER = 'add_lighting';

/** `addedBy` of the vanity light bar bathroom post-processing generates. */
const GENERATED_VANITY_LIGHT_PRODUCER = 'bathroom-post-processing';

/** One of the pipeline's generated ceiling cans. */
const isGeneratedCeilingLight = (o) => o.addedBy === GENERATED_LIGHT_PRODUCER;

/** One of the pipeline's generated vanity lights (a bar, or a sconce). */
const isGeneratedWallLight = (o) =>
  o.addedBy === GENERATED_VANITY_LIGHT_PRODUCER &&
  (o.type === 'overhead-wall-light' || o.type === 'wall-light');

/**
 * Standard single-gang plates — the same sizes as the electrical catalog
 * (web-app `utils/electricalDevices.js` ELECTRICAL_DEVICES; a web test keeps
 * the two equal). The centre sits ON the wall face: the device renderers
 * build the plate outward from local z = 0.
 */
const PLATE = { x: 0.07, y: 0.115, z: 0.04 };
const SWITCH_PLATE = { x: 0.07, y: 0.11, z: 0.04 };

/** A 4" recessed can, as `add_lighting` sizes one. */
const RECESSED_CAN = { x: 0.1016, y: 0.0254, z: 0.1016 };

/**
 * Bounds on a measured light or fan width (`extentM`), metres, and the width
 * used when there is no usable measurement. A light's is the generic dome's
 * standard 12" (the user, 2026-10-16), at its height.
 */
const LIGHT_WIDTH = {
  min: 0.1,
  max: 0.9,
  fallback: GENERIC_DOME.widthInches * 0.0254,
  height: GENERIC_DOME.heightInches * 0.0254,
};

/** The generic builds a found light can be, by style. */
const GENERIC_BUILDS = new Map(
  genericCeilingLightBuilds().map((b) => [b.style, b])
);
const FAN_WIDTH = { min: 0.6, max: 1.6, fallback: 1.07, height: 0.3 };
const EXHAUST_FAN = { x: 0.26, y: 0.04, z: 0.26 };
const WALL_LIGHT = { x: 0.15, y: 0.25, z: 0.12 };

/**
 * How each inspected type becomes an object. `mount: 'wall'` needs the wall
 * the device is on (a wall-face doc); `onFace` says where its centre sits
 * relative to that face — `'face'` on it (outlets, switches: the renderers
 * build the plate outward), `'depth'` half its depth into the room (a wall
 * light, whose config seats it by its own depth). `mount: 'ceiling'` hangs it
 * from the world point, which the pass stores at the ATTACHMENT point.
 */
const OBJECT_SPECS = {
  outlet: { mount: 'wall', type: 'outlet', dims: PLATE, onFace: 'face' },
  'gfci-outlet': {
    mount: 'wall',
    type: 'gfci-outlet',
    dims: PLATE,
    onFace: 'face',
  },
  'outlet-240v': {
    mount: 'wall',
    type: 'outlet-240v',
    dims: PLATE,
    onFace: 'face',
  },
  switch: { mount: 'wall', type: 'switch', dims: SWITCH_PLATE, onFace: 'face' },
  dimmer: { mount: 'wall', type: 'dimmer', dims: SWITCH_PLATE, onFace: 'face' },
  '3way': { mount: 'wall', type: '3way', dims: SWITCH_PLATE, onFace: 'face' },
  'wall-light': {
    mount: 'wall',
    type: 'wall-light',
    dims: WALL_LIGHT,
    onFace: 'depth',
  },
  'ceiling-light': { mount: 'ceiling', type: 'ceiling-light', light: true },
  // Where it was found is not recorded as a mount; the electrical layer draws
  // it as a ceiling light, and so does this.
  'emergency-light': { mount: 'ceiling', type: 'ceiling-light', light: true },
  'ceiling-fan': { mount: 'ceiling', type: 'ceiling-fan', fan: true },
  'exhaust-fan': { mount: 'ceiling', type: 'exhaust-fan', dims: EXHAUST_FAN },
};

/**
 * The light subtypes an object carries as `lightingSubtype`, each with the
 * words that name it. The pass's `subtype` is the model's own phrase, not the
 * vocabulary's word — "round surface-mounted", "flush-mount LED disc",
 * "flush square" on the corpus — so it is matched by keyword, first match in
 * this order (a "recessed" phrase is a can whatever else it says).
 */
const OBJECT_LIGHT_SUBTYPES = [
  ['recessed', /\brecess|\bcan\b|downlight/],
  ['chandelier', /chandelier/],
  ['pendant', /pendant/],
  ['track', /\btrack/],
  ['flush', /flush|surface/],
];

/** The object subtype a device's phrase names, or null. */
function lightSubtypeOf(phrase) {
  if (typeof phrase !== 'string') return null;
  const words = phrase.toLowerCase();
  const hit = OBJECT_LIGHT_SUBTYPES.find(([, re]) => re.test(words));
  return hit ? hit[0] : null;
}

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const isPoint = (w) => w && isNum(w.x) && isNum(w.y) && isNum(w.z);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const r4 = (v) => Math.round(v * 1e4) / 1e4;

/** The room a doc was written for: by id, else by its scan room index. */
/** How far outside its room's floor outline a device may sit (a wall device is ON the edge). */
const ROOM_EDGE_TOL_M = 0.05;

/**
 * The room a device goes into: the doc's room by id; else, among the rooms
 * with the doc's scan room index, the one whose floor holds the device.
 *
 * Not simply the first room with that index. A satellite room (a closet or
 * nook carved off a scanned room, `parentRoomId`) shares its parent's
 * `roomIndex`, and its id is minted anew on every reprocess — so a doc
 * written for the satellite names a room that no longer exists, and the
 * first room with the index is the PARENT. 6F51BDA5's "Room 0" lights went
 * into its kitchen that way. A device in none of them is left out.
 */
function roomForDevice(doc, device, rooms) {
  const byId = doc.roomId && rooms.find((r) => r && r.id === doc.roomId);
  if (byId) return byId;
  if (!Number.isInteger(doc.roomIndex)) return null;
  const sameScanRoom = rooms.filter((r) => r && r.roomIndex === doc.roomIndex);
  const point = { x: device.world.x, z: device.world.z };
  return (
    sameScanRoom.find((r) => {
      const outline = polygonOf(r);
      return (
        outline &&
        outline.length >= 3 &&
        isPointInsideOrOnPolygon(point, outline, ROOM_EDGE_TOL_M)
      );
    }) || null
  );
}

/**
 * The devices of the docs that may become objects: a surface the model judged
 * (or one it saw too little of to judge, which keeps the devices it did see —
 * the outlets on a backsplash), never a device another surface keeps
 * (`duplicateOf`), never one the contractor hid on this run's review.
 */
function* candidateDevices(docs) {
  for (const doc of docs || []) {
    if (!doc) continue;
    if (doc.verdict !== 'observed' && doc.reason !== 'model-says-unseen') {
      continue;
    }
    const hidden = new Set(activeReview(doc).hiddenDevices);
    for (const device of doc.devices || []) {
      if (!device || !device.id || device.duplicateOf) continue;
      if (hidden.has(device.id)) continue;
      yield { doc, device };
    }
  }
}

/** The point on a wall face nearest `p`, in plan; `y` kept. */
function onFaceLine(p, faceEnds) {
  const [a, b] = faceEnds;
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len2 = dx * dx + dz * dz;
  if (!(len2 > 1e-12)) return { x: p.x, y: p.y, z: p.z };
  const t = clamp(((p.x - a.x) * dx + (p.z - a.z) * dz) / len2, 0, 1);
  return { x: a.x + t * dx, y: p.y, z: a.z + t * dz };
}

/** The wall a wall-mounted device is on, from its wall-face doc; null off one. */
function wallOf(doc) {
  if (doc.kind !== 'wall-face') return null;
  const anchor = doc.anchor || {};
  const n = anchor.faceNormal;
  const ends = anchor.faceEnds;
  if (!n || !isNum(n.x) || !isNum(n.z)) return null;
  if (!Array.isArray(ends) || ends.length !== 2) return null;
  const len = Math.hypot(n.x, n.z);
  if (!(len > 1e-9)) return null;
  return {
    wallId: doc.elementId || null,
    normal: { x: n.x / len, z: n.z / len },
    faceEnds: ends,
  };
}

/**
 * Whether a device's `extentM` measures the FIXTURE. A device the pass found
 * as a lit-light candidate (`world.source: 'candidate'`) was sized by its
 * bright blob — the glow, not the fitting: 2.4 cm for 6F51BDA5's kitchen LED
 * disc, 5.5 cm for its bathroom flush mount. A size the model outlined
 * itself is the fixture's.
 */
const measuresFixture = (device) =>
  isNum(device.extentM) &&
  device.extentM > 0 &&
  !(device.world && device.world.source === 'candidate');

/** A measured width within `bounds`, or the fallback when none was measured. */
function widthOf(device, bounds) {
  return measuresFixture(device)
    ? clamp(device.extentM, bounds.min, bounds.max)
    : bounds.fallback;
}

/** The common fields of every object this step makes. */
function baseObject(doc, device, spec, label) {
  return {
    id: `inspected-${doc.targetId}-${device.id}`,
    type: spec.type,
    label,
    addedBy: INSPECTED_PRODUCER,
    // an existing condition: what the house already has, unpriced to install
    [SCANNED_FIELD]: true,
    inspection: {
      runId: doc.runId || null,
      targetId: doc.targetId || null,
      deviceId: device.id,
      deviceType: device.type,
      confidence: device.confidence || null,
    },
  };
}

/** A wall-mounted device's object, or a skip reason. */
function wallObject(doc, device, spec, label) {
  const wall = wallOf(doc);
  if (!wall) return { skip: 'no-wall' };
  const { normal } = wall;
  const onFace = onFaceLine(device.world, wall.faceEnds);
  const out = spec.onFace === 'depth' ? spec.dims.z / 2 : 0;
  const object = {
    ...baseObject(doc, device, spec, label),
    category:
      spec.type === 'wall-light'
        ? { 'wall-light': true }
        : { electrical: true, [spec.type]: true },
    center: {
      x: r4(onFace.x + normal.x * out),
      y: r4(onFace.y),
      z: r4(onFace.z + normal.z * out),
    },
    dimensions: { ...spec.dims },
    // local +Z along the wall's inward normal, as every wall fixture
    rotation: { x: 0, y: r4(Math.atan2(normal.x, normal.z)), z: 0 },
    parentWallIds: wall.wallId ? [wall.wallId] : [],
  };
  return { object };
}

/**
 * A ceiling-mounted device's object, hung from the ceiling above its
 * attachment point. The pass measures WHERE on the ceiling a device is well,
 * but its height only to the pass's placement error (a light in 6F51BDA5's
 * bathroom read 9 cm under a flat ceiling), and an attachment point is on the
 * ceiling by definition — so the height is the room's ceiling at that point,
 * by the rule the generated cans are seated with (`ceilingSeat`). Only where
 * no ceiling covers the point does the measured height stand.
 */
function ceilingObject(doc, device, spec, label, room) {
  const base = baseObject(doc, device, spec, label);
  let dims;
  const extra = {};
  if (spec.light) {
    const subtype = lightSubtypeOf(device.subtype);
    const hung = GENERIC_BUILDS.get(subtype);
    if (subtype === 'recessed') {
      dims = { ...RECESSED_CAN };
      extra.proceduralStyle = 'recessed';
    } else if (hung && hung.style !== 'dome') {
      // the generic pendant / chandelier, hung on the standard stem (the
      // pass does not measure a drop): its build's fields and box, at the
      // measured width when the measurement is the fixture's
      const w = measuresFixture(device)
        ? clamp(device.extentM, LIGHT_WIDTH.min, LIGHT_WIDTH.max)
        : hung.dimensions.x;
      dims = { x: w, y: hung.dimensions.y, z: w };
      Object.assign(extra, hung.fields);
    } else {
      // the generic flush-mount dome (web `GenericCeilingLight`) at its
      // measured width; with no style the scene draws a fixed 4" puck
      const w = widthOf(device, LIGHT_WIDTH);
      dims = { x: w, y: LIGHT_WIDTH.height, z: w };
      extra.proceduralStyle = 'dome';
    }
    if (subtype) extra.lightingSubtype = subtype;
    extra.category = { 'ceiling-light': true };
  } else if (spec.fan) {
    const w = widthOf(device, FAN_WIDTH);
    dims = { x: w, y: FAN_WIDTH.height, z: w };
    extra.category = { electrical: true, 'ceiling-fan': true };
  } else {
    dims = { ...spec.dims };
    extra.category = { electrical: true, [spec.type]: true };
  }
  const w = device.world;
  const ceilingY = makeCeilingYLookup(room.ceilings, null)(w.x, w.z);
  const topY = ceilingY == null ? w.y : ceilingY;
  const parentCeilingId = makeCeilingIdLookup(room.ceilings)(w.x, w.z);
  return {
    object: {
      ...base,
      ...extra,
      // top flush at the ceiling, as `add_lighting` seats a can or a drum
      center: { x: r4(w.x), y: r4(topY - dims.y / 2), z: r4(w.z) },
      dimensions: dims,
      rotation: { x: 0, y: 0, z: 0 },
      ...(parentCeilingId ? { parentCeilingId } : {}),
    },
  };
}

/** A found shower head is a hand shower: by the model's phrase, or a hose point. */
const isHandShower = (device) =>
  /hand/i.test(device.subtype || '') || isPoint(device.hoseWorld);

/**
 * Where a found shower head puts the room's own showerhead (2026-10-19): a
 * patch for that object, or a skip reason.
 *
 * The pipeline gives every shower and tub/shower combo a head, a member of
 * the fixture's group (`fixtureGroupId` + `parents`), which is how the
 * design tools swap, rebuild and resize it with its shower. So a found head
 * MOVES that object rather than adding a second one beside it; its id, group,
 * producer and catalog model stay. Its point is where it is attached (the
 * arm leaving the wall, or a hand shower's holder), put on the wall face; the
 * box's back goes on the wall there, as the procedural layouts draw it
 * (`showerLayout.js`: wall plane at z = -depth/2). A fixed head's box TOP is
 * at the arm (its flange is at the top of the layout); a hand shower's holder
 * is at the box's middle, and where its hose leaves the wall is recorded in
 * the head's own frame (`hoseOutlet`, metres along local +x and up from the
 * box centre, on the wall plane) so it moves with the head. No catalog
 * product is a hand shower, so it is drawn as the app's own.
 */
function showerHeadPatch(doc, device, object) {
  const wall = wallOf(doc);
  if (!wall) return { skip: 'no-wall' };
  const { normal } = wall;
  const at = onFaceLine(device.world, wall.faceEnds);
  const dims = object.dimensions || {};
  const out = isNum(dims.z) ? dims.z / 2 : 0;
  const hand = isHandShower(device);
  const down = hand || !isNum(dims.y) ? 0 : dims.y / 2;
  const patch = {
    center: {
      x: r4(at.x + normal.x * out),
      y: r4(at.y - down),
      z: r4(at.z + normal.z * out),
    },
    // local +Z along the wall's inward normal, as every wall fixture
    rotation: { x: 0, y: r4(Math.atan2(normal.x, normal.z)), z: 0 },
    parentWallIds: wall.wallId ? [wall.wallId] : [],
    inspection: baseObject(doc, device, { type: object.type }, null).inspection,
  };
  if (hand) {
    patch.subtype = 'handheld';
    patch.isProcedural = true;
    if (isPoint(device.hoseWorld)) {
      const hose = onFaceLine(device.hoseWorld, wall.faceEnds);
      // local +x is (n.z, -n.x) in plan for a rotation of atan2(n.x, n.z)
      patch.hoseOutlet = {
        x: r4((hose.x - at.x) * normal.z - (hose.z - at.z) * normal.x),
        y: r4(hose.y - at.y),
      };
    }
  }
  return { patch };
}

/** Distance from an object's centre to a point, metres. */
const distanceTo = (object, p) =>
  Math.hypot(
    object.center.x - p.x,
    object.center.y - p.y,
    object.center.z - p.z
  );

/**
 * Each room's found shower heads matched to its showerheads, nearest pair
 * first, one each: a bathroom with two showers has two heads, and two finds
 * of one head (the fold missed it) must not both move it.
 */
function matchShowerHeads(found) {
  const pairs = [];
  found.forEach((f, i) => {
    for (const o of f.room.objects || []) {
      if (o && o.type === 'showerhead' && o.center && isPoint(o.center)) {
        pairs.push({ i, object: o, d: distanceTo(o, f.device.world) });
      }
    }
  });
  pairs.sort((a, b) => a.d - b.d);
  const taken = new Set();
  const match = new Array(found.length).fill(null);
  for (const { i, object } of pairs) {
    if (match[i] || taken.has(object.id)) continue;
    match[i] = object;
    taken.add(object.id);
  }
  return match;
}

/**
 * What applying the inspection does to each room.
 *
 * @param {object} args
 * @param {Array<object>} args.docs  existing-conditions docs (`targetId`,
 *   `roomId`/`roomIndex`, `kind`, `anchor`, `devices[]`, …)
 * @param {Array<object>} args.rooms the rooms the devices go into, each with
 *   `id` (and `roomIndex` to resolve a doc that names no room id)
 * @param {(type: string) => string} [args.labelOf] a device type in words
 * @returns {{
 *   rooms: Array<{ roomId: string, add: object[],
 *     reseat: Array<{ id: string, patch: object }>,
 *     replacesGeneratedLights: boolean, replacesGeneratedWallLights: boolean }>,
 *   skipped: Record<string, number>,
 * }}
 *   Only rooms that gain a device or move a showerhead are listed (`reseat`:
 *   the room's own showerhead moved to a found shower head,
 *   `showerHeadPatch`). `skipped` counts devices left out, by reason:
 *   `not-electrical`, `no-point`, `off-target`, `no-room`, `no-wall`,
 *   `no-showerhead`.
 */
function planInspectedDevices({ docs, rooms, labelOf = (t) => t }) {
  const byRoom = new Map();
  const skipped = {};
  const skip = (reason) => {
    skipped[reason] = (skipped[reason] || 0) + 1;
  };
  const entryFor = (room) => {
    if (!byRoom.has(room.id)) {
      byRoom.set(room.id, {
        roomId: room.id,
        add: [],
        reseat: [],
        replacesGeneratedLights: false,
        replacesGeneratedWallLights: false,
      });
    }
    return byRoom.get(room.id);
  };
  const showerHeads = [];
  for (const { doc, device } of candidateDevices(docs)) {
    const spec = OBJECT_SPECS[device.type];
    const showerHead = device.type === 'shower-head';
    if (!spec && !showerHead) {
      skip('not-electrical');
      continue;
    }
    if (!isPoint(device.world)) {
      skip('no-point');
      continue;
    }
    // Its depth point is more than OFF_TARGET_M from the surface it was
    // reported on: seen elsewhere in the shot, and the point is wherever the
    // depth ray landed, which need not be the device. 6F51BDA5's Room 0 saw a
    // flush light over the top of a wall; the ray hit the wall, 71 cm below
    // the ceiling. It stays on the report and the electrical changes layer.
    if (device.offTarget) {
      skip('off-target');
      continue;
    }
    const room = roomForDevice(doc, device, rooms || []);
    if (!room) {
      skip('no-room');
      continue;
    }
    if (showerHead) {
      showerHeads.push({ doc, device, room });
      continue;
    }
    const label = labelOf(device.type);
    const made =
      spec.mount === 'wall'
        ? wallObject(doc, device, spec, label)
        : ceilingObject(doc, device, spec, label, room);
    if (made.skip) {
      skip(made.skip);
      continue;
    }
    const entry = entryFor(room);
    entry.add.push(made.object);
    if (spec.light && spec.mount === 'ceiling') {
      entry.replacesGeneratedLights = true;
    }
    if (spec.type === 'wall-light') {
      entry.replacesGeneratedWallLights = true;
    }
  }
  matchShowerHeads(showerHeads).forEach((object, i) => {
    const { doc, device, room } = showerHeads[i];
    if (!object) {
      skip('no-showerhead');
      return;
    }
    const made = showerHeadPatch(doc, device, object);
    if (made.skip) {
      skip(made.skip);
      return;
    }
    entryFor(room).reseat.push({ id: object.id, patch: made.patch });
  });
  return { rooms: [...byRoom.values()], skipped };
}

/** The same value, whatever order an object's keys were written in. */
function sameValue(a, b) {
  if (a === b) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => sameValue(a[k], b[k]));
}

/**
 * A showerhead moved by its patch. `hoseOutlet` is this step's alone, so a
 * patch without one (a fixed head, or a run that did not find the hose)
 * takes an earlier apply's away.
 */
function reseated(object, patch) {
  const { hoseOutlet, ...rest } = object;
  return { ...rest, ...patch };
}

/**
 * A room with the plan applied: every object an earlier apply made removed,
 * the generated cans and vanity lights removed where the room's plan replaces
 * them (and any wiring or group link that named them), its showerheads moved
 * to the shower heads found, and the plan's objects added. A room the plan does
 * not list loses only an earlier apply's objects.
 *
 * @returns {{ room: object, changed: boolean, removedGeneratedLights: number,
 *   reseatedShowerheads: number }}
 */
function applyToRoom(room, roomPlan) {
  const patches = new Map(
    ((roomPlan && roomPlan.reseat) || []).map((r) => [r.id, r.patch])
  );
  let reseatedShowerheads = 0;
  let moved = false;
  const objects = (room.objects || []).map((o) => {
    const patch = o && patches.get(o.id);
    if (!patch) return o;
    reseatedShowerheads += 1;
    const next = reseated(o, patch);
    if (sameValue(next, o)) return o;
    moved = true;
    return next;
  });
  const replacesCeiling = Boolean(roomPlan && roomPlan.replacesGeneratedLights);
  const replacesWall = Boolean(
    roomPlan && roomPlan.replacesGeneratedWallLights
  );
  const isReplacedGuess = (o) =>
    (replacesCeiling && isGeneratedCeilingLight(o)) ||
    (replacesWall && isGeneratedWallLight(o));
  const removed = new Set(
    objects
      .filter(
        (o) => o && (o.addedBy === INSPECTED_PRODUCER || isReplacedGuess(o))
      )
      .map((o) => o.id)
  );
  const add = (roomPlan && roomPlan.add) || [];
  if (!removed.size && !add.length) {
    return moved
      ? {
          room: { ...room, objects },
          changed: true,
          removedGeneratedLights: 0,
          reseatedShowerheads,
        }
      : {
          room,
          changed: false,
          removedGeneratedLights: 0,
          reseatedShowerheads,
        };
  }
  const removedGeneratedLights = objects.filter(
    (o) => o && removed.has(o.id) && isReplacedGuess(o)
  ).length;
  const next = {
    ...room,
    // A generated vanity light is a member of its vanity's group, whose
    // anchor links name it (mirror centred on it, it framed by the mirror):
    // drop the links its removal leaves pointing at nothing.
    objects: pruneDanglingGroupLinks([
      ...objects.filter((o) => !o || !removed.has(o.id)),
      ...add,
    ]),
  };
  const connections = room.electrical && room.electrical.connections;
  if (Array.isArray(connections)) {
    const kept = connections.filter(
      (c) => !c || !(removed.has(c.switchId) || removed.has(c.fixtureId))
    );
    if (kept.length !== connections.length) {
      next.electrical = { ...room.electrical, connections: kept };
    }
  }
  return {
    room: next,
    changed: true,
    removedGeneratedLights,
    reseatedShowerheads,
  };
}

/**
 * Every room with the plan applied, and what changed.
 *
 * @returns {{ rooms: object[], changedRoomIds: string[],
 *   added: number, removedGeneratedLights: number,
 *   reseatedShowerheads: number }}
 */
function applyInspectedDevices(rooms, plan) {
  const byId = new Map((plan.rooms || []).map((p) => [p.roomId, p]));
  const changedRoomIds = [];
  let added = 0;
  let removedGeneratedLights = 0;
  let reseatedShowerheads = 0;
  const next = (rooms || []).map((room) => {
    const roomPlan = byId.get(room.id);
    const result = applyToRoom(room, roomPlan);
    if (result.changed) changedRoomIds.push(room.id);
    added += roomPlan ? roomPlan.add.length : 0;
    removedGeneratedLights += result.removedGeneratedLights;
    reseatedShowerheads += result.reseatedShowerheads;
    return result.room;
  });
  return {
    rooms: next,
    changedRoomIds,
    added,
    removedGeneratedLights,
    reseatedShowerheads,
  };
}

module.exports = {
  INSPECTED_PRODUCER,
  GENERATED_LIGHT_PRODUCER,
  GENERATED_VANITY_LIGHT_PRODUCER,
  INSPECTED_OBJECT_TYPES: Object.keys(OBJECT_SPECS),
  INSPECTED_PLATE_SIZES: { outlet: PLATE, switch: SWITCH_PLATE },
  roomForDevice,
  planInspectedDevices,
  applyInspectedDevices,
};
